import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, ArrowRight, Mail, Lock, User as UserIcon, Building2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { useBranding } from "@/hooks/use-branding";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(1, "Requerida"),
  remember: z.boolean().optional(),
});

const registerSchema = z.object({
  name: z.string().min(2, "Mínimo 2 caracteres"),
  last_name: z.string().optional(),
  company: z.string().min(2, "Requerido"),
  email: z.string().email("Email inválido"),
  password: z.string().min(8, "Mínimo 8 caracteres"),
  confirm: z.string(),
}).refine((d) => d.password === d.confirm, {
  message: "Las contraseñas no coinciden",
  path: ["confirm"],
});

type LoginInput = z.infer<typeof loginSchema>;
type RegisterInput = z.infer<typeof registerSchema>;

function LoginPage() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();
  const branding = useBranding();

  const loginForm = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "", remember: true },
  });

  const registerForm = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: "", last_name: "", company: "", email: "", password: "", confirm: "" },
  });

  async function onLogin(data: LoginInput) {
    try {
      await api.login(data.email, data.password, data.remember ?? true);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate({ to: "/dashboard" });
    } catch (e) {
      const msg = (e as Error).message || "Credenciales inválidas";
      toast.error(msg);
    }
  }

  async function onRegister(data: RegisterInput) {
    try {
      await api.register({
        name: data.name,
        last_name: data.last_name,
        company: data.company,
        email: data.email,
        password: data.password,
      });
      await api.login(data.email, data.password, true);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate({ to: "/dashboard" });
    } catch (e) {
      const msg = (e as Error).message || "No se pudo crear la cuenta";
      toast.error(msg);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-ink-50 dark:bg-ink-950">
      <div className="flex-1 grid lg:grid-cols-2">
        {/* Brand pane */}
        <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 text-white">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-warn/30 blur-3xl" />
          <div className="absolute top-20 -left-20 w-72 h-72 rounded-full bg-brand-300/30 blur-3xl" />
          <div className="relative flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.brand_name} className="w-9 h-9 rounded" />
            ) : (
              <div className="tm-logo-mark" style={{ width: 36, height: 36 }} />
            )}
            <span className="text-xl font-semibold tracking-tight">{branding.brand_name}</span>
          </div>
          <div className="relative max-w-md">
            {branding.login_tagline && (
              <p className="text-xs uppercase tracking-widest text-brand-100/80 mb-3">{branding.login_tagline}</p>
            )}
            {branding.login_headline && (
              <h1 className="text-4xl font-bold leading-tight whitespace-pre-line">{branding.login_headline}</h1>
            )}
            {branding.login_subtitle && (
              <p className="mt-4 text-brand-100/90 leading-relaxed whitespace-pre-line">{branding.login_subtitle}</p>
            )}
            {branding.login_stats && branding.login_stats.length > 0 && (
              <div className="mt-8 grid grid-cols-3 gap-4">
                {branding.login_stats.map((s, i) => (
                  <div key={`${s.l}-${i}`} className="rounded-xl bg-white/10 backdrop-blur-sm ring-1 ring-white/15 px-4 py-3">
                    <div className="text-2xl font-bold">{s.n}</div>
                    <div className="text-[11px] text-brand-100/80">{s.l}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {branding.login_compliance && (
            <div className="relative flex items-center gap-3 text-sm text-brand-100/80">
              <ShieldCheck size={16} />
              <span>{branding.login_compliance}</span>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="flex flex-col justify-center px-6 sm:px-12 py-10 bg-white dark:bg-ink-900">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.brand_name} className="w-8 h-8 rounded" />
            ) : (
              <div className="tm-logo-mark" />
            )}
            <span className="font-semibold text-ink-900 dark:text-white">{branding.brand_name}</span>
          </div>

          <div className="mx-auto w-full max-w-sm">
            <div className="flex bg-ink-100 dark:bg-ink-800 rounded-lg p-1 mb-6">
              {[
                { id: "login" as const, label: "Iniciar sesión" },
                { id: "register" as const, label: "Crear cuenta" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setMode(t.id)}
                  className={`flex-1 h-8 rounded-md text-sm font-medium transition-colors ${
                    mode === t.id
                      ? "bg-white dark:bg-ink-900 text-ink-900 dark:text-white shadow-sm"
                      : "text-ink-500"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {mode === "login" ? (
              <>
                <h2 className="text-2xl font-bold text-ink-900 dark:text-white">Bienvenido de vuelta</h2>
                <p className="mt-1 text-sm text-ink-500">Ingresa con tu cuenta para continuar</p>

                <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-3 mt-6">
                  <div>
                    <Label>Correo</Label>
                    <div className="mt-1 relative">
                      <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                      <Input type="email" className="pl-10" {...loginForm.register("email")} />
                    </div>
                    {loginForm.formState.errors.email && (
                      <p className="text-xs text-bad mt-1">{loginForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <Label>Contraseña</Label>
                    <div className="mt-1 relative">
                      <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                      <Input
                        type={showPass ? "text" : "password"}
                        className="pl-10 pr-10"
                        {...loginForm.register("password")}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                      >
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-2 text-ink-600 dark:text-ink-300 cursor-pointer">
                      <input
                        type="checkbox"
                        defaultChecked
                        className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                        {...loginForm.register("remember")}
                      />
                      Recordarme
                    </label>
                    <a className="text-brand-600 hover:text-brand-700 font-medium" href="#">
                      ¿Olvidaste tu contraseña?
                    </a>
                  </div>
                  <Button size="lg" className="w-full mt-2" disabled={loginForm.formState.isSubmitting}>
                    {loginForm.formState.isSubmitting ? "Verificando…" : (
                      <>
                        Entrar <ArrowRight size={16} />
                      </>
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-ink-900 dark:text-white">Crea tu cuenta</h2>
                {branding.login_register_tagline && (
                  <p className="mt-1 text-sm text-ink-500">{branding.login_register_tagline}</p>
                )}

                <form onSubmit={registerForm.handleSubmit(onRegister)} className="space-y-3 mt-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Nombre</Label>
                      <div className="mt-1 relative">
                        <UserIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                        <Input className="pl-10" {...registerForm.register("name")} />
                      </div>
                    </div>
                    <div>
                      <Label>Apellido</Label>
                      <Input className="mt-1" {...registerForm.register("last_name")} />
                    </div>
                  </div>
                  <div>
                    <Label>Empresa</Label>
                    <div className="mt-1 relative">
                      <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                      <Input className="pl-10" {...registerForm.register("company")} />
                    </div>
                  </div>
                  <div>
                    <Label>Correo profesional</Label>
                    <Input type="email" className="mt-1" {...registerForm.register("email")} />
                    {registerForm.formState.errors.email && (
                      <p className="text-xs text-bad mt-1">{registerForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <Label>Contraseña</Label>
                    <Input type="password" className="mt-1" {...registerForm.register("password")} />
                    {registerForm.formState.errors.password && (
                      <p className="text-xs text-bad mt-1">{registerForm.formState.errors.password.message}</p>
                    )}
                  </div>
                  <div>
                    <Label>Confirmar contraseña</Label>
                    <Input type="password" className="mt-1" {...registerForm.register("confirm")} />
                    {registerForm.formState.errors.confirm && (
                      <p className="text-xs text-bad mt-1">{registerForm.formState.errors.confirm.message}</p>
                    )}
                  </div>
                  <Button size="lg" className="w-full mt-2" disabled={registerForm.formState.isSubmitting}>
                    {registerForm.formState.isSubmitting ? "Creando…" : "Crear cuenta"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      <footer className="border-t border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 px-6 py-4 flex items-center justify-between text-xs text-ink-500">
        <div>© 2026 {branding.brand_name}. Todos los derechos reservados.</div>
        <div className="flex items-center gap-4">
          <a href="#" className="hover:text-ink-700">Privacidad</a>
          <a href="#" className="hover:text-ink-700">Términos</a>
          <a href="#" className="hover:text-ink-700">Soporte</a>
        </div>
      </footer>
    </div>
  );
}
